import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type {
  AppVersionPolicyCommand,
  AppVersionPolicyUpdateInput,
} from "@/api/appVersionPolicies";
import type { Capabilities } from "@/auth/rbac";
import { EnvBadge } from "@/components/EnvBanner";
import { env } from "@/config/env";
import type { AppPlatform, AppVersionPolicyDoc } from "@/firestore/appVersionPolicies";
import {
  BUILD_FIELD_LABELS,
  MAX_BUILD,
  MAX_MESSAGE_LENGTH,
  MAX_REASON_LENGTH,
  MAX_UNCONFIRMED_RAISE,
  PLATFORM_LABELS,
  STORE_AVAILABILITY_LABELS,
  STORE_URL_HOSTS,
  SUPER_ADMIN_HINT,
  defaultGracePreset,
  describeChange,
  formatEffectiveAt,
  gracePresets,
  isLargeJump,
  isRaise,
  isShortGrace,
  parseBuild,
  policyActionError,
  resolveGrace,
  shortensGrace,
  validMessage,
  validReason,
  validStoreUrl,
  type GracePreset,
  type ResolvedGrace,
} from "./presentation";

export interface AppVersionPolicyDialogProps {
  platform: AppPlatform;
  /** The live projection right now; null when creating (or if the row vanished mid-edit). */
  current: AppVersionPolicyDoc | null;
  /** `current.updatedAt` captured when the dialog opened (null when creating) — the stale guard. */
  expectedUpdatedAt: string | null;
  capabilities: Capabilities;
  pending: boolean;
  onSubmit(command: AppVersionPolicyCommand): Promise<unknown>;
  onClose(): void;
}

/** Only the fields the operator actually changed go on the wire (the PATCH body is a subset). */
function updatePatch(
  baseline: AppVersionPolicyDoc,
  values: {
    requiredBuild: number;
    recommendedBuild: number;
    grace: ResolvedGrace;
    storeUrl: string;
    message: string;
  },
): Omit<AppVersionPolicyUpdateInput, "reason" | "confirmRequiredBuild"> {
  const patch: Omit<AppVersionPolicyUpdateInput, "reason" | "confirmRequiredBuild"> = {};
  if (values.requiredBuild !== baseline.requiredBuild) patch.requiredBuild = values.requiredBuild;
  if (values.recommendedBuild !== baseline.recommendedBuild) {
    patch.recommendedBuild = values.recommendedBuild;
  }
  if (values.grace.kind === "value" && values.grace.value !== baseline.requiredEffectiveAt) {
    patch.requiredEffectiveAt = values.grace.value;
  }
  const storeUrl = values.storeUrl.trim() || null;
  if (storeUrl !== baseline.storeUrl) patch.storeUrl = storeUrl;
  const message = values.message.trim() || null;
  if (message !== baseline.message) patch.message = message;
  return patch;
}

export function AppVersionPolicyDialog(props: AppVersionPolicyDialogProps) {
  const { platform, current, capabilities } = props;
  const label = PLATFORM_LABELS[platform];
  // Snapshot what the operator reviewed; a live change is surfaced by the stale guard, never merged.
  const [baseline] = useState(() => current);
  const creating = baseline === null;
  const [requiredBuild, setRequiredBuild] = useState<number | string>(baseline?.requiredBuild ?? 0);
  const [recommendedBuild, setRecommendedBuild] = useState<number | string>(
    baseline?.recommendedBuild ?? 0,
  );
  const [gracePreset, setGracePreset] = useState<GracePreset>(defaultGracePreset(false, !creating));
  const [graceTouched, setGraceTouched] = useState(false);
  const [customGrace, setCustomGrace] = useState("");
  const [storeUrl, setStoreUrl] = useState(baseline?.storeUrl ?? "");
  const [message, setMessage] = useState(baseline?.message ?? "");
  const [reason, setReason] = useState("");
  const [confirmRequiredBuild, setConfirmRequiredBuild] = useState<number | string>("");
  const [attestAvailable, setAttestAvailable] = useState(false);
  const [attestBlocked, setAttestBlocked] = useState(false);
  const [attestIncident, setAttestIncident] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const now = Date.now();
  const stale = (current?.updatedAt ?? null) !== props.expectedUpdatedAt;
  const busy = props.pending || submitting;

  const required = parseBuild(requiredBuild);
  const recommended = parseBuild(recommendedBuild);
  const raise = required !== null && isRaise(baseline, required);
  const grace = resolveGrace(gracePreset, customGrace, now);
  const nextEffectiveAt =
    grace.kind === "value" ? grace.value : (baseline?.requiredEffectiveAt ?? null);
  const shortens = grace.kind === "value" && shortensGrace(baseline, grace.value, now);
  const needsSuperAdmin = raise || shortens;
  const largeJump = required !== null && isLargeJump(baseline, required);
  const gated = required !== null && required > 0;
  const shortGrace = gated && isShortGrace(nextEffectiveAt, now);
  const needsIncidentAttestation = env.isProd && shortGrace && needsSuperAdmin;
  const attested =
    (!raise || (attestAvailable && attestBlocked)) && (!needsIncidentAttestation || attestIncident);

  const storeUrlOk = validStoreUrl(storeUrl);
  const messageOk = validMessage(message);
  const reasonOk = validReason(reason);
  const confirmOk = !largeJump || parseBuild(confirmRequiredBuild) === required;
  const fieldsOk =
    required !== null &&
    recommended !== null &&
    recommended >= required &&
    grace.kind !== "invalid" &&
    storeUrlOk &&
    messageOk;
  const patch =
    baseline && required !== null && recommended !== null
      ? updatePatch(baseline, {
          requiredBuild: required,
          recommendedBuild: recommended,
          grace,
          storeUrl,
          message,
        })
      : null;
  const changed = creating || (patch !== null && Object.keys(patch).length > 0);
  const valid = fieldsOk && reasonOk && confirmOk && changed;

  // Mirrors the server: any admin lowers / extends; only superAdmin raises / shortens, in every env.
  const disabledReason = !capabilities.act
    ? "Your role can't change app version policies."
    : needsSuperAdmin && !capabilities.superAdmin
      ? SUPER_ADMIN_HINT
      : env.isProd && !capabilities.actProd
        ? "Your role can't act in production."
        : null;
  const canSubmit = disabledReason === null && valid && attested && !stale && !busy;

  function resetAttestations() {
    setAttestAvailable(false);
    setAttestBlocked(false);
    setAttestIncident(false);
  }

  function onRequiredChange(value: number | string) {
    setRequiredBuild(value);
    resetAttestations();
    if (!graceTouched) {
      const next = parseBuild(value);
      setGracePreset(defaultGracePreset(next !== null && isRaise(baseline, next), !creating));
    }
  }

  async function submit() {
    if (!canSubmit || required === null || recommended === null) return;
    // Presets are relative — resolve against the submit instant, not the render one.
    const graceAtSubmit = resolveGrace(gracePreset, customGrace, Date.now());
    if (graceAtSubmit.kind === "invalid") return;
    const confirm = largeJump ? { confirmRequiredBuild: required } : {};
    let command: AppVersionPolicyCommand;
    if (baseline === null) {
      command = {
        kind: "create",
        input: {
          platform,
          requiredBuild: required,
          recommendedBuild: recommended,
          requiredEffectiveAt: graceAtSubmit.kind === "value" ? graceAtSubmit.value : null,
          message: message.trim() || null,
          storeUrl: storeUrl.trim() || null,
          reason: reason.trim(),
          ...confirm,
        },
      };
    } else {
      command = {
        kind: "update",
        id: baseline.id,
        updatedAt: baseline.updatedAt,
        input: {
          ...updatePatch(baseline, {
            requiredBuild: required,
            recommendedBuild: recommended,
            grace: graceAtSubmit,
            storeUrl,
            message,
          }),
          reason: reason.trim(),
          ...confirm,
        },
      };
    }
    setSubmitting(true);
    setError(null);
    try {
      await props.onSubmit(command);
      props.onClose();
    } catch (e) {
      setError(policyActionError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const submitLabel = creating ? "Create policy" : "Save policy";
  const submitButton = (
    <Button
      type="submit"
      color={needsSuperAdmin ? "red" : "blue"}
      disabled={!canSubmit}
      loading={busy}
    >
      {submitLabel}
    </Button>
  );

  return (
    <Modal
      opened
      title={`${creating ? "Create" : "Edit"} ${label} app version policy`}
      onClose={() => {
        if (!busy) props.onClose();
      }}
      closeOnEscape={!busy}
      closeOnClickOutside={!busy}
      withCloseButton={!busy}
      size="lg"
      centered
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Stack>
          {env.isProd ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Group gap="xs">
                You are changing the {label} gate in <EnvBadge size="sm" />. Users below the
                required build lose access to the app until they update.
              </Group>
            </Alert>
          ) : null}
          <Text size="sm" c="dimmed">
            Builds are compared as whole numbers ({BUILD_FIELD_LABELS[platform]}). The required
            build is the hard floor the backend enforces (0 = no gate); the recommended build only
            shows a dismissable nudge.
          </Text>
          <Group grow align="flex-start">
            <NumberInput
              label="Required build"
              description="Hard floor. 0 disables the gate."
              value={requiredBuild}
              onChange={onRequiredChange}
              min={0}
              max={MAX_BUILD}
              allowDecimal={false}
              allowNegative={false}
              disabled={busy}
              required
              error={requiredBuild !== "" && required === null ? "Enter a whole number" : undefined}
            />
            <NumberInput
              label="Recommended build"
              description="Soft floor; at least the required build."
              value={recommendedBuild}
              onChange={(value) => {
                setRecommendedBuild(value);
                resetAttestations();
              }}
              min={0}
              max={MAX_BUILD}
              allowDecimal={false}
              allowNegative={false}
              disabled={busy}
              required
              error={
                recommendedBuild !== "" && recommended === null
                  ? "Enter a whole number"
                  : recommended !== null && required !== null && recommended < required
                    ? "Must be at least the required build"
                    : undefined
              }
            />
          </Group>
          {largeJump ? (
            <NumberInput
              label="Retype the required build to confirm"
              description={
                creating
                  ? `A new policy with a hard floor must be retyped to rule out a typo.`
                  : `This raises the floor by more than ${MAX_UNCONFIRMED_RAISE} — retype ${required} to rule out a typo.`
              }
              value={confirmRequiredBuild}
              onChange={setConfirmRequiredBuild}
              min={0}
              max={MAX_BUILD}
              allowDecimal={false}
              allowNegative={false}
              disabled={busy}
              required
              error={
                confirmRequiredBuild !== "" && !confirmOk
                  ? "Does not match the required build"
                  : undefined
              }
            />
          ) : null}
          <NativeSelect
            label="Required floor takes effect"
            description="Grace period before builds below the required floor are blocked."
            value={gracePreset}
            onChange={(event) => {
              setGracePreset(event.currentTarget.value as GracePreset);
              setGraceTouched(true);
              resetAttestations();
            }}
            data={gracePresets(!creating)}
            disabled={busy}
          />
          {gracePreset === "custom" ? (
            <TextInput
              type="datetime-local"
              label="Custom date and time"
              description="Your local time; at most 90 days ahead."
              value={customGrace}
              onChange={(event) => {
                setCustomGrace(event.currentTarget.value);
                resetAttestations();
              }}
              disabled={busy}
              error={grace.kind === "invalid" ? grace.reason : undefined}
              required
            />
          ) : null}
          <TextInput
            label="Store link override (optional)"
            description={`https:// on ${STORE_URL_HOSTS.join(", ")}. Blank uses the link compiled into the app.`}
            value={storeUrl}
            onChange={(event) => setStoreUrl(event.currentTarget.value)}
            disabled={busy}
            spellCheck={false}
            error={storeUrlOk ? undefined : "Use an https:// link on an allow-listed store host."}
          />
          <Textarea
            label="Message shown to users (optional)"
            description={`Shown under the default copy on the update screen. ${message.trim().length}/${MAX_MESSAGE_LENGTH}`}
            value={message}
            onChange={(event) => setMessage(event.currentTarget.value)}
            disabled={busy}
            minRows={2}
            autosize
            error={messageOk ? undefined : `At most ${MAX_MESSAGE_LENGTH} characters.`}
          />
          <Textarea
            label="Reason (required)"
            description={`Recorded for audit against your operator account; never shown to users. ${reason.trim().length}/${MAX_REASON_LENGTH}`}
            placeholder="Why is this floor changing?"
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            disabled={busy}
            minRows={2}
            autosize
            required
            error={
              reason.length > 0 && !reasonOk ? `1 to ${MAX_REASON_LENGTH} characters.` : undefined
            }
          />
          {required !== null && recommended !== null ? (
            <Text size="sm" fw={500}>
              {describeChange(
                platform,
                baseline,
                {
                  requiredBuild: required,
                  recommendedBuild: recommended,
                  requiredEffectiveAt: nextEffectiveAt,
                },
                now,
              )}
            </Text>
          ) : null}
          {shortGrace && needsSuperAdmin ? (
            <Alert color="orange" variant="light">
              The required floor takes effect less than 24 hours from now. Users below build{" "}
              {required} will be blocked almost immediately.
            </Alert>
          ) : null}
          {raise ? (
            <>
              <Checkbox
                checked={attestAvailable}
                onChange={(event) => setAttestAvailable(event.currentTarget.checked)}
                disabled={busy}
                label={`Build ${required} is ${STORE_AVAILABILITY_LABELS[platform]}.`}
              />
              <Checkbox
                checked={attestBlocked}
                onChange={(event) => setAttestBlocked(event.currentTarget.checked)}
                disabled={busy}
                label={`I understand users below build ${required} will be blocked from ${formatEffectiveAt(nextEffectiveAt, now)}.`}
              />
            </>
          ) : null}
          {needsIncidentAttestation ? (
            <Checkbox
              checked={attestIncident}
              onChange={(event) => setAttestIncident(event.currentTarget.checked)}
              disabled={busy}
              label="This is a security incident and immediate blocking is intended."
            />
          ) : null}
          {needsSuperAdmin && !capabilities.superAdmin ? (
            <Alert color="yellow" variant="light" title="Super-admin only">
              {SUPER_ADMIN_HINT}
            </Alert>
          ) : null}
          {stale ? (
            <Alert color="orange">
              This policy changed while you were editing. Close the dialog, review the latest
              values, and reopen it.
            </Alert>
          ) : null}
          {error ? (
            <Alert color="red">
              <Text size="sm">{error}</Text>
            </Alert>
          ) : null}
          <Text size="xs" c="dimmed">
            Takes effect within about a minute on the API and seconds on devices. Any admin can
            lower the required build or extend the grace period; raising it or shortening the grace
            period needs the superAdmin role.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" disabled={busy} onClick={props.onClose}>
              Cancel
            </Button>
            {disabledReason ? (
              <Tooltip label={disabledReason} multiline w={320} withArrow>
                <span>{submitButton}</span>
              </Tooltip>
            ) : (
              submitButton
            )}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
